import React, { useState, useEffect } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import DateRangeFilter from './components/DateRangeFilter';
import SummaryCards from './components/SummaryCards';
import RevenueTrendChart from './components/RevenueTrendChart';
import TechnicianRevenueChart from './components/TechnicianRevenueChart';
import VehicleTypeChart from './components/VehicleTypeChart';
import TechnicianPerformanceTable from './components/TechnicianPerformanceTable';
import ExportButtons from './components/ExportButtons';
import { reportingService } from '../../services/reportingService';

const AdvancedReporting = () => {
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('last30days');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [reportData, setReportData] = useState({
    summary: {},
    technicianBreakdown: [],
    vehicleTypeBreakdown: [],
    dailyTrend: [],
    rawData: []
  });

  useEffect(() => {
    // Initialize with last 30 days
    const today = new Date();
    const last30 = new Date(today);
    last30?.setDate(last30?.getDate() - 30);
    
    setDateRange({
      startDate: last30?.toISOString()?.split('T')?.[0],
      endDate: today?.toISOString()?.split('T')?.[0]
    });
  }, []);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      loadReportData();
    }
  }, [dateRange]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const data = await reportingService?.getDetailedReportData(
        dateRange?.startDate,
        dateRange?.endDate
      );
      setReportData(data);
    } catch (error) {
      console.error('Error loading report data:', error);
      setReportData({
        summary: {},
        technicianBreakdown: [],
        vehicleTypeBreakdown: [],
        dailyTrend: [],
        rawData: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDateRange) => {
    setDateRange(prev => ({ ...prev, ...newDateRange }));
  };

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      
      <main className="flex-1 overflow-hidden ml-0 lg:ml-64">
        <div className="h-full flex flex-col">
          {/* Header */}
          <header className="bg-card border-b border-border px-6 py-4 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0 ml-10 lg:ml-0">
                  <Icon name="BarChart3" size={24} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl lg:text-2xl font-heading font-bold text-foreground leading-tight">
                    Reportes Avanzados
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Análisis detallado de ingresos y desempeño
                  </p>
                </div>
              </div>
              <ExportButtons 
                reportData={reportData} 
                loading={loading}
                dateRange={dateRange}
              />
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Date Range Filter */}
              <DateRangeFilter
                startDate={dateRange?.startDate}
                endDate={dateRange?.endDate}
                onDateChange={handleDateChange}
                preset={preset}
                onPresetChange={handlePresetChange}
              />

              {/* Summary Cards */}
              <SummaryCards summary={reportData?.summary} loading={loading} />

              {/* Revenue Trend Chart */}
              <RevenueTrendChart data={reportData?.dailyTrend} loading={loading} />

              {/* Technician Revenue Chart */}
              <TechnicianRevenueChart 
                data={reportData?.technicianBreakdown} 
                loading={loading}
                dateRange={dateRange}
              />

              {/* Vehicle Type Chart */}
              <VehicleTypeChart 
                data={reportData?.vehicleTypeBreakdown} 
                loading={loading}
                dateRange={dateRange}
              />

              {/* Technician Performance Table */}
              <TechnicianPerformanceTable 
                data={reportData?.technicianBreakdown} 
                loading={loading} 
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdvancedReporting;